import { deleteItemLog, downloadFileLogActivity, search } from "../api/ProjectApi";
import { fromJS } from "immutable";
import { useEffect, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { isValid, differenceInCalendarDays } from "date-fns";
import { formatInTimeZone } from "date-fns-tz";
import Tabs from "../components/ui/Tabs";
import type { TabItem as UITabItem } from "../components/ui/Tabs";
import {
  ActivityIcon,
  BadgeDollarSign,
  BoxesIcon,
  Bubbles,
  BuildingIcon,
  FolderOpenIcon,
  ImageIcon,
  MailboxIcon,
  NotepadTextIcon,
  PanelsTopLeftIcon,
  Pencil,
  SquareCheckBigIcon,
  SquareKanbanIcon,
  Trash2,
  UsersIcon,
} from "lucide-react";
import { SmallLoading } from "../main/Loading";
import ActivityAction from "../actions/ActivityAction";
import { markAsDoneActivity } from "../api/ProjectApi";
import { ActivityTypes, ContentTypes, DialogTypes } from "../constants/action-types";
import { openLink } from "../api/AppLinkApi";
import JiraTaskIcon from "../JiraTaskIcon";
import ItemLinkView from "../main/ItemLinkView";
import { useTranslation } from "react-i18next";
import ItemOverview from "./ItemOverview";
import TaskAction from "../actions/TaskAction";
import NotesSection from "../main/item-view/Right/NotesSection";
import FilesSection from "../main/item-view/Right/FilesSection";
import EmailsSection from "../main/item-view/Right/EmailsSection";
import TasksSection from "../main/item-view/Right/TasksSection";
import type { TaskRow } from "../main/item-view/Right/TasksSection";
import PricesSection from "../main/item-view/Right/PricesSection";
import VariationsSection from "../main/item-view/Right/VariationsSection";
import ImageView from "../ai-content/components/ImageView";
import { formatAmount } from "../api/DataTypeUtils";
import ActivityView, { formatDistanceToNowActivity } from "./ActivityView";
import { isGmailContext } from "../api/api";
import ProductsPicker from "../main/component/ProductsPicker";
import ConfirmDialog from "../dialog/ConfirmDialog";

export const ActivityTabs = {
  OVERVIEW: {
    key: "overview",
    value: "Overview",
    icon: <PanelsTopLeftIcon className={"w-4 h-4"} />,
    groups: [ContentTypes.SALE, ContentTypes.CONTACT, ContentTypes.COMPANY],
  },
  ACTIVITY: {
    key: "createActivity",
    value: "Activity",
    icon: <ActivityIcon className={"w-4 h-4"} />,
    groups: [ContentTypes.SALE, ContentTypes.CONTACT, ContentTypes.COMPANY],
  },
  TASKS: {
    key: "task",
    value: "Tasks",
    icon: <SquareCheckBigIcon className={"w-4 h-4"} />,
    groups: [ContentTypes.SALE, ContentTypes.CONTACT, ContentTypes.COMPANY],
  },
  NOTE: {
    key: "commented",
    value: "Notes",
    icon: <NotepadTextIcon className={"w-4 h-4"} />,
    groups: [ContentTypes.SALE, ContentTypes.CONTACT, ContentTypes.COMPANY],
  },
  EMAIL: {
    key: "sendEmail",
    value: "Emails",
    icon: <MailboxIcon className={"w-4 h-4"} />,
    groups: [ContentTypes.SALE, ContentTypes.CONTACT, ContentTypes.COMPANY],
  },
  FILE: {
    key: "uploadFile",
    value: "Files",
    icon: <FolderOpenIcon className={"w-4 h-4"} />,
    groups: [ContentTypes.SALE, ContentTypes.CONTACT, ContentTypes.COMPANY, ContentTypes.PRODUCT],
  },
  ISSUE: {
    key: "issue",
    value: "Issues",
    icon: <JiraTaskIcon className={"w-4 h-4"} />,
    groups: [ContentTypes.SALE],
  },
  CONTACT: {
    key: "contact",
    value: "Contacts",
    icon: <UsersIcon className={"w-4 h-4"} />,
    groups: [ContentTypes.SALE],
  },
  COMPANY: {
    key: "company",
    value: "Accounts",
    icon: <BuildingIcon className={"w-4 h-4"} />,
    groups: [ContentTypes.SALE, ContentTypes.CONTACT],
  },
  TEAM: {
    key: "team",
    value: "Teams",
    icon: <UsersIcon className={"w-4 h-4"} />,
    groups: [ContentTypes.COMPANY],
  },
  DEAL: {
    key: "deal",
    value: "Deals",
    icon: <SquareKanbanIcon className={"w-4 h-4"} />,
    groups: [ContentTypes.CONTACT, ContentTypes.COMPANY],
  },
  PRODUCT: {
    key: "product",
    value: "Products",
    icon: <BoxesIcon className={"w-4 h-4"} />,
    groups: [ContentTypes.SALE],
  },
  PRICE: {
    key: "prices",
    value: "Prices",
    icon: <BadgeDollarSign className={"w-4 h-4"} />,
    groups: [],
  },
  VARIATION: {
    key: "variations",
    value: "Variations",
    icon: <Bubbles className={"w-4 h-4"} />,
    groups: [],
  },
  DEAL_PRODUCT: {
    key: "dealProduct",
    value: "Deals",
    icon: <SquareKanbanIcon className={"w-4 h-4"} />,
    groups: [ContentTypes.PRODUCT],
  },
};

const TAB_ORDER = [
  "overview",
  "createActivity",
  "task",
  "commented",
  "sendEmail",
  "prices",
  "variations",
  "dealProduct",
  "uploadFile",
  "product",
  "issue",
  "contact",
  "company",
  "team",
  "deal",
];

const ItemActivityLogs = ({
  projectId,
  item = fromJS({}),
  itemId,
  itemType,
  itemActivities = fromJS([]),
  allActivities = fromJS([]),
  appGoogleData = fromJS({}),
  appAzureData = fromJS({}),
  showHeader = true,
  activityAction = ActivityTabs.ACTIVITY.key,
  isLoading = false,
}) => {
  const { t, i18n } = useTranslation();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const tenantInfo = useSelector((state: any) => state.tenant.get("tenantInfo"));
  const isJira = tenantInfo.getIn(["host", "product"]) === "JIRA";
  const upcomingFilters =
    useSelector((state: any) => state.activityReducer.get("upcomingFilters")) || fromJS({});
  const projectCurrency = useSelector((state: any) =>
    state.projectReducer.getIn(["project", "currency"])
  );
  const currency = projectCurrency || "USD";
  const members = useSelector((state: any) => state.projectReducer.getIn(["project", "members"]));
  const buckets = useSelector((state: any) => state.bucketReducer.get("SALE")) || fromJS([]);
  const itemLinks =
    useSelector((state: any) => state.itemLinkReducer.get("itemLinks")) || fromJS([]);

  const hasGmail =
    (appGoogleData.get("connected") || false) &&
    (appGoogleData.getIn(["data", "googleSetting", "gmail"]) || false);
  const hasOutlook = appAzureData.get("connected") || false;

  // Ref to programmatically open the ProductsPicker from per-row edit buttons
  const productPickerOpenRef = useRef<((open: boolean) => void) | null>(null);
  // Index of product pending delete confirmation (-1 = none)
  const [confirmDeleteProductIdx, setConfirmDeleteProductIdx] = useState<number>(-1);

  const [activeTab, setActiveTab] = useState({
    action: activityAction,
    loading: false,
  });

  const { data: salesByProduct } = useQuery({
    queryKey: ["sales-by-product", projectId, itemId],
    queryFn: async () => {
      return await search({
        projectId: projectId,
        pageSize: 50,
        types: ["SALE"],
        filters: {
          products: {
            operator: "in",
            values: [itemId],
          },
        },
      });
    },
    enabled: !!projectId && itemType === "PRODUCT" && !!itemId,
  });

  useEffect(() => {
    setActiveTab({ action: activityAction, loading: false });
  }, [activityAction]);

  const getMember = (members, accountId) => {
    return (
      members.find((member) => member.get("accountId") == accountId) ||
      fromJS({ accountId, displayName: accountId })
    );
  };

  const handDeleteLog = (logId) => {
    deleteItemLog(itemId, logId).then(() => {
      dispatch(
        ActivityAction.searchActivities({
          itemId,
          scope: "item",
          includeDependencies:
            itemType === "COMPANY" || itemType === "CONTACT" || itemType === "SALE",
        })
      );
    });
  };

  const handleViewLog = (activityItem: any) => {
    const eventType = activityItem.getIn(["event", "data", "activityType"]);
    if (eventType == "file") {
      const fileName = activityItem.getIn(["event", "data", "fileName"]);
      downloadFileLogActivity(fileName).then(openLink);
    } else {
      dispatch(
        ActivityAction.showActivityDialog(activityItem.get("itemId"), activityItem.get("id"))
      );
    }
  };

  const renderOverview = () => {
    const handleSelectStage = (funnelId: string) => {
      if (!itemId || !funnelId) return;
      dispatch(TaskAction.updateTask(itemId, "bucketId", funnelId));
    };
    return (
      <ItemOverview
        itemActivities={itemActivities}
        allActivities={allActivities}
        itemLinks={itemLinks}
        item={item}
        hasGmail={hasGmail}
        hasOutlook={hasOutlook}
        onSelectStage={handleSelectStage}
        onNavigateTab={(key: string) => {
          setActiveTab({ action: key, loading: false });
        }}
        isLoading={isLoading}
      />
    );
  };

  const computeDueLabel = (start: any, startTime: string) => {
    if (!start) return "";
    let dt: Date;
    // Handle both timestamp (number) and date string
    if (typeof start === "number") {
      // If it's a timestamp in milliseconds, use it directly
      dt = new Date(start);
    } else if (typeof start === "string") {
      // If it's a date string, combine with startTime if provided
      const dtStr = `${start}${startTime ? `T${startTime}` : ""}`;
      dt = new Date(dtStr);
    } else {
      return "";
    }
    if (!isValid(dt)) return "";
    const days = differenceInCalendarDays(dt, new Date());
    if (days === 0) return "@Due today";
    if (days > 0) return `@Due in ${days} day${days > 1 ? "s" : ""}`;
    const ago = Math.abs(days);
    return `@Due ${ago} day${ago > 1 ? "s" : ""} ago`;
  };

  const getPlainText = (html: string) => {
    if (!html) return "";
    const div = document.createElement("div");
    div.innerHTML = html;
    return div.textContent || div.innerText || "";
  };

  const renderTasksSection = () => {
    const tasks: TaskRow[] = itemActivities
      .filter((log) => log.getIn(["event", "data", "activityType"]) === ActivityTypes.task)
      .map((log) => {
        const createdAt = log.getIn(["event", "createdAt"]);
        const summary = log.getIn(["event", "data", "summary"]) || t("Task");
        const timeZone = log.getIn(["event", "data", "timeZone"]);
        let start = log.getIn(["event", "data", "start"]);
        if (typeof start === "number") {
          start = formatInTimeZone(
            start,
            timeZone || Intl.DateTimeFormat().resolvedOptions().timeZone,
            "yyyy-MM-dd"
          );
        }
        const startTime = log.getIn(["event", "data", "startTime"]) || "";
        const dueLabel = computeDueLabel(start, startTime);
        // Use assignees instead of attendees for tasks
        const assigneesRaw = log.getIn(["event", "data", "assignees"]) || [];
        // map assignees into simple avatar/name placeholders if available from members
        let assignees: { name?: string; avatarUrl?: string }[] = [];
        try {
          const raw = assigneesRaw?.toJS?.() ?? assigneesRaw;
          const arr = Array.isArray(raw) ? raw : [raw];
          assignees = arr
            .map((acc: any) => {
              // Handle both string accountId and object with accountId
              const accountId = typeof acc === "string" ? acc : acc?.accountId || acc;
              // Use getMember function to get member info (same as other components)
              const member = getMember(members, accountId);
              return {
                name: member.get("displayName"),
                avatarUrl: member.get("avatarUrl"),
              };
            })
            .filter(Boolean);
        } catch {}
        const done = log.getIn(["event", "data", "done"]) || false;
        return {
          id: log.get("id"),
          title: summary,
          createdAt,
          dueAt: typeof start === "number" ? Math.floor(start / 1000) : undefined, // Convert to seconds epoch if timestamp
          dueLabel,
          overdue: dueLabel.includes("ago"),
          assignees,
          completed: done,
        } as TaskRow;
      })
      .toArray();

    const handleOpen = (rowId: string | number) => {
      const act = itemActivities.find((l) => l.get("id") === rowId);
      if (act) handleViewLog(act);
    };

    const handleToggle = (rowId: string | number) => {
      const act = itemActivities.find((l) => l.get("id") === rowId);
      if (!act || !itemId) return;
      const current = act.getIn(["event", "data", "done"]) || false;
      const next = !current;
      markAsDoneActivity(itemId, String(rowId), next)
        .then(() => {
          dispatch(
            ActivityAction.searchActivities({
              itemId,
              scope: "item",
              includeDependencies:
                itemType === "COMPANY" || itemType === "CONTACT" || itemType === "SALE",
            })
          );
          if (isGmailContext()) {
            dispatch(ActivityAction.searchUpcomingChrome(projectId, upcomingFilters));
          }
        })
        .catch(() => {
          // swallow for now; could add notification if needed
        });
    };

    const handleAddTask = () => {
      if (!item.get("id")) return;
      dispatch(
        ActivityAction.showActivityDialog(item.get("id"), null, {
          item: {
            value: item.get("id"),
            label: item.getIn(["data", "name", "value"]),
            type: item.get("type"),
          },
          activityType: ActivityTypes.task,
          summary: "",
        })
      );
    };

    return (
      <TasksSection
        title={t("Tasks")}
        tasks={tasks}
        onAddTask={handleAddTask}
        onOpen={handleOpen}
        onToggleComplete={handleToggle}
        onDelete={(rowId) => handDeleteLog(rowId)}
      />
    );
  };

  const renderNotesSection = () => {
    const notes = itemActivities
      .filter((log) => log.getIn(["event", "data", "activityType"]) === ActivityTypes.commented)
      .map((log) => {
        const member = getMember(members, log.getIn(["event", "person"]));
        const title = log.getIn(["event", "data", "summary"]) || t("Note");
        const titlePreview = title.substring(0, 120);
        const description = log.getIn(["event", "data", "description"]) || "";
        const descriptionPreview = getPlainText(description.replace(/&nbsp;/g, " ")).substring(
          0,
          240
        );
        return {
          id: log.get("id"),
          author: member.get("displayName"),
          avatarUrl: member.get("avatarUrl"),
          timeLabel: formatDistanceToNowActivity(log),
          title,
          titlePreview: titlePreview.length < 120 ? titlePreview : `${titlePreview}...`,
          description,
          descriptionPreview:
            descriptionPreview.length < 240 ? descriptionPreview : `${descriptionPreview}...`,
        };
      })
      .toArray();

    const handleAddNote = () => {
      if (!item.get("id")) return;
      const primaryContactId =
        item.getIn(["data", "contact", "value", "id"]) ||
        item.getIn(["data", "contact", "value", "value"]);
      dispatch(
        ActivityAction.showActivityDialog(item.get("id"), null, {
          item: {
            value: item.get("id"),
            label: item.getIn(["data", "name", "value"]),
            type: item.get("type"),
          },
          attendees: item.getIn(["data", "email", "value"])
            ? [item.getIn(["data", "email", "value"])]
            : [],
          contactId: primaryContactId,
          activityType: ActivityTypes.commented,
          summary: "Note",
        })
      );
    };

    const handleCopyNote = (noteId: string) => {
      const note = notes.find((n: any) => n.id === noteId);
      if (!note) return;
      try {
        const text = `${note.title}\n\n${note.description}`;
        navigator.clipboard?.writeText(text);
      } catch {}
    };

    const handleShareNote = (noteId: string) => {
      const note = notes.find((n: any) => n.id === noteId);
      if (!note) return;
      const nav: any = navigator as any;
      if (nav && typeof nav.share === "function") {
        try {
          const p = nav.share({ title: note.title, text: note.description });
          if (p && typeof p.then === "function") {
            p.catch((err: any) => {
              // Ignore when user cancels the native share sheet
              if (
                err &&
                (err.name === "AbortError" ||
                  err.message?.includes("Abort") ||
                  err.message?.toLowerCase?.().includes("cancel"))
              ) {
                return;
              }
              // Fallback: copy to clipboard on failure
              try {
                navigator.clipboard?.writeText(`${note.title}\n\n${note.description}`);
              } catch {}
              console.warn("Share failed:", err);
            });
          }
        } catch (err: any) {
          if (
            err &&
            (err.name === "AbortError" ||
              err.message?.includes("Abort") ||
              err.message?.toLowerCase?.().includes("cancel"))
          ) {
            return;
          }
          try {
            navigator.clipboard?.writeText(`${note.title}\n\n${note.description}`);
          } catch {}
          console.warn("Share threw:", err);
        }
      } else {
        // Fallback if Web Share API not available
        try {
          navigator.clipboard?.writeText(`${note.title}\n\n${note.description}`);
        } catch {}
      }
    };

    const handleEditNote = (noteId: string) => {
      if (!itemId) return;
      dispatch(ActivityAction.showActivityDialog(itemId, noteId));
    };

    return (
      <NotesSection
        notes={notes}
        onAddNote={handleAddNote}
        onEditNote={handleEditNote}
        onDeleteNote={(noteId: string) => handDeleteLog(noteId)}
        onCopyNote={handleCopyNote}
        onShareNote={handleShareNote}
      />
    );
  };

  const renderEmailsSection = () => {
    const emails = itemActivities
      .filter(
        (log) =>
          log.getIn(["event", "data", "activityType"]) === ActivityTypes.email ||
          log.getIn(["event", "data", "activityType"]) === ActivityTypes.logEmail ||
          log.getIn(["event", "data", "activityType"]) === ActivityTypes.sendEmail ||
          log.getIn(["event", "data", "activityType"]) === ActivityTypes.sendDraftEmail ||
          log.getIn(["event", "data", "activityType"]) === ActivityTypes.sendScheduledEmail ||
          log.getIn(["event", "data", "activityType"]) === ActivityTypes.recordSentEmail
      )
      .map((log) => {
        const createdAt = log.getIn(["event", "createdAt"]);
        const subject = log.getIn(["event", "data", "summary"]) || "No Subject";
        const description = log.getIn(["event", "data", "description"]) || "";
        const attendees = log.getIn(["event", "data", "attendees"])?.toJS() || [];
        const person = getMember(members, log.getIn(["event", "person"]));
        const preview = getPlainText(description).substring(0, 140);
        const uniqueAttendees = [...new Set<string>(attendees)];
        return {
          id: log.get("id"),
          subject,
          to: uniqueAttendees.join(", "),
          recipients: uniqueAttendees,
          preview,
          timeLabel: formatDistanceToNowActivity(log),
          createdAt,
          senderName: person.get("displayName"),
          senderAvatar: person.get("avatarUrl"),
          trackingEnabled: log.getIn(["event", "data", "tracking"]) || false,
          visibility: log.getIn(["event", "data", "visibility"]) || undefined,
          messageId: log.getIn(["event", "data", "messageId"]) || undefined,
          threadId: log.getIn(["event", "data", "threadId"]) || undefined,
          provider: log.getIn(["event", "data", "hasGmail"]) ? "GMAIL" : "OUTLOOK",
          isLogEmail: log.getIn(["event", "data", "activityType"]) === ActivityTypes.logEmail,
          isRecordSentEmail:
            log.getIn(["event", "data", "activityType"]) === ActivityTypes.recordSentEmail,
        } as any;
      })
      .toArray();

    const handleOpen = (rowId: string | number) => {
      if (!rowId) return;
      dispatch(ActivityAction.showActivityDialog(item.get("id"), rowId));
    };

    return (
      <EmailsSection
        title={t("Emails")}
        emails={emails as any}
        hasGmail={hasGmail}
        hasOutlook={hasOutlook}
        onOpen={handleOpen}
        onLogEmail={() => {
          const primaryContactId =
            item.getIn(["data", "contact", "value", "id"]) ||
            item.getIn(["data", "contact", "value", "value"]);
          dispatch(
            ActivityAction.showActivityDialog(item.get("id"), null, {
              item: {
                value: item.get("id"),
                label: item.getIn(["data", "name", "value"]),
                type: item.get("type"),
              },
              attendees: item.getIn(["data", "email", "value"])
                ? [item.getIn(["data", "email", "value"])]
                : [],
              contactId: primaryContactId,
              activityType: ActivityTypes.logEmail,
              summary: "",
            })
          );
        }}
        onComposeEmail={() => {
          const primaryContactId =
            item.getIn(["data", "contact", "value", "id"]) ||
            item.getIn(["data", "contact", "value", "value"]);
          dispatch(
            ActivityAction.showActivityDialog(item.get("id"), null, {
              item: {
                value: item.get("id"),
                label: item.getIn(["data", "name", "value"]),
                type: item.get("type"),
              },
              attendees: item.getIn(["data", "email", "value"])
                ? [item.getIn(["data", "email", "value"])]
                : [],
              contactId: primaryContactId,
              activityType: ActivityTypes.sendEmail,
              summary: "",
            })
          );
        }}
        onTrackingEmail={(messageId, threadId) => {
          dispatch(
            TaskAction.showRightPanelDialog(
              {
                messageId,
                threadId,
              },
              DialogTypes.sideMyInboxMessageDialog
            )
          );
        }}
      />
    );
  };

  const renderActivitySection = () => {
    const activities = allActivities.filter(
      (item) =>
        item.getIn(["event", "data", "emailStatus"]) !== "DRAFT" &&
        !(item.getIn(["event", "action"]) === "edited" && !item.getIn(["event", "fieldId"]))
    );
    return <ActivityView item={item} activities={activities} />;
  };

  const renderFilesSection = () => {
    const files = itemActivities
      .filter((log) => log.getIn(["event", "data", "activityType"]) === ActivityTypes.file)
      .map((log) => {
        const member = getMember(members, log.getIn(["event", "person"]));
        const visibility = log.getIn(["event", "data", "visibility"]) || undefined;
        const fileName = log.getIn(["event", "data", "fileName"]) || "File";
        const fileSize = log.getIn(["event", "data", "fileSize"]) || "0 bytes";
        const fileContentType =
          log.getIn(["event", "data", "fileContentType"]) || "application/pdf";
        return {
          id: log.get("id"),
          visibility,
          name: fileName,
          size: fileSize,
          sizeType: fileContentType,
          uploaderName: member.get("displayName"),
          uploaderAvatar: member.get("avatarUrl"),
          timeLabel: formatDistanceToNowActivity(log),
        } as any;
      })
      .toArray();

    const handleOpen = (rowId: string | number) => {
      const row: any = files.find((f: any) => f.id === rowId);
      if (!row) return;
      const fileName = row.name;
      if (!fileName) return;
      downloadFileLogActivity(fileName).then(openLink);
    };

    return (
      <FilesSection
        title={t("Files")}
        files={files as any}
        onUpload={() => {
          // mở dialog upload file
          if (!item.get("id")) return;
          dispatch(
            ActivityAction.showActivityDialog(item.get("id"), null, {
              item: {
                value: item.get("id"),
                label: item.getIn(["data", "name", "value"]),
                type: item.get("type"),
              },
              activityType: ActivityTypes.file,
            })
          );
        }}
        onUploadSuccess={() => {
          dispatch(
            ActivityAction.searchActivities({
              itemId,
              scope: "item",
              includeDependencies:
                itemType === "COMPANY" || itemType === "CONTACT" || itemType === "SALE",
            })
          );
        }}
        onOpen={handleOpen}
        onDelete={(rowId) => handDeleteLog(rowId)}
      />
    );
  };

  const renderProduct = () => {
    const productsImmutable = item.getIn(["data", "products", "value"]) || fromJS([]);
    const productsPlain: any[] =
      typeof productsImmutable?.toJS === "function"
        ? productsImmutable.toJS()
        : productsImmutable || [];

    const handleSaveProducts = async (products: any[], totalAmount: any) => {
      const itemId = item.get("id");
      if (!itemId) return;
      await dispatch(TaskAction.updateTask(itemId, "estimatedRevenue", totalAmount));
      await dispatch(TaskAction.updateTask(itemId, "products", products));
    };

    const handleRemoveProduct = (idx: number) => {
      const newProducts = productsPlain.filter((_, i) => i !== idx);
      const newTotal = newProducts.reduce(
        (sum: number, p: any) => sum + p.unitPrice * p.quantity,
        0
      );
      handleSaveProducts(newProducts, newTotal);
    };

    return (
      <>
        <div className="w-full flex flex-col gap-3">
          <div className="flex items-start justify-between pb-2 border-b border-gray-200">
            <h2 className="text-lg font-semibold text-gray-900">{t("Products")}</h2>
            {/* Hidden picker — exposes setIsOpen via ref for per-row pencil icon */}
            <ProductsPicker
              projectId={projectId}
              currency={currency}
              renderTarget={(_isOpen, setIsOpen, hasProducts) => {
                productPickerOpenRef.current = setIsOpen;
                return (
                  <button
                    onClick={() => setIsOpen(true)}
                    className={
                      hasProducts
                        ? "hidden"
                        : "inline-flex items-center gap-1.5 px-2 py-1.5 bg-white border border-gray-300 rounded-md shadow-xs hover:bg-gray-50 transition-colors text-xs font-medium text-gray-900"
                    }
                  >
                    {t("Add products")}
                  </button>
                );
              }}
              value={productsPlain}
              onChange={handleSaveProducts}
            />
          </div>
          <div className="flex flex-col divide-y divide-gray-100">
            {productsPlain.map((product: any, idx: number) => {
              const imageIds: string[] = product.imageIds || [];
              const renderAvatar =
                imageIds.length > 0 ? (
                  <ImageView fileName={imageIds[0]} className="w-10 h-10 object-cover p-1" />
                ) : (
                  <ImageIcon className="w-10 h-10 text-gray-300" />
                );

              return (
                <div
                  key={product.id || idx}
                  className="group flex items-center py-2 gap-2 hover:bg-gray-50 transition-colors rounded-md"
                >
                  <div className="flex items-center gap-2 min-w-0 flex-1">
                    {renderAvatar}
                    <button
                      type="button"
                      className="truncate text-sm font-medium text-blue-600 hover:text-blue-700 hover:underline transition-colors text-left mr-3 cursor-pointer"
                      title={product.name}
                      onClick={() => {
                        navigate(`/workspaces/${projectId}/items/${product.id}?type=PRODUCT`);
                      }}
                    >
                      {product.name}
                    </button>
                  </div>
                  <div className="min-w-0 flex-1 text-sm text-gray-600">{product.productCode}</div>
                  <div className="min-w-0 flex-1 text-sm text-gray-600">
                    {formatAmount(i18n.language, product.unitPrice, currency)}
                  </div>
                  <div className="min-w-0 flex-1 text-sm text-gray-600">
                    {t("Quantity")}: {product.quantity}
                  </div>
                  <div className="min-w-0 flex-1 text-sm text-gray-600">
                    {t("Tax")}: {product.tax}%
                  </div>
                  {/* Edit / Remove actions */}
                  <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity shrink-0">
                    <button
                      title={t("Edit products")}
                      onClick={() => productPickerOpenRef.current?.(true)}
                      className="p-1 rounded hover:bg-gray-200 transition-colors text-gray-500 hover:text-blue-600"
                    >
                      <Pencil className="w-3.5 h-3.5" />
                    </button>
                    <button
                      title={t("Remove")}
                      onClick={() => setConfirmDeleteProductIdx(idx)}
                      className="p-1 rounded hover:bg-red-50 transition-colors text-red-500 hover:text-red-600"
                    >
                      <Trash2 className="w-3.5 h-3.5" />
                    </button>
                  </div>
                </div>
              );
            })}
            {productsPlain.length === 0 && (
              <div className="py-8 text-center text-sm text-gray-600">{t("No products yet")}</div>
            )}
          </div>
        </div>

        <ConfirmDialog
          visible={confirmDeleteProductIdx >= 0}
          title={t("Remove product")}
          description={t("Are you sure you want to remove this product from the deal?")}
          dangerBtn
          submitBtnLabel={t("Remove")}
          onClose={() => setConfirmDeleteProductIdx(-1)}
          onSubmit={() => {
            handleRemoveProduct(confirmDeleteProductIdx);
            setConfirmDeleteProductIdx(-1);
          }}
        />
      </>
    );
  };

  const renderPrices = () => {
    const prices = itemActivities
      .filter((log) => log.getIn(["event", "data", "activityType"]) === ActivityTypes.price)
      .map((log) => {
        const member = getMember(members, log.getIn(["event", "person"]));
        const title = log.getIn(["event", "data", "summary"]) || t("Note");
        const titlePreview = title.substring(0, 120);
        const description = log.getIn(["event", "data", "description"]) || "";
        const descriptionPreview = getPlainText(description.replace(/&nbsp;/g, " ")).substring(
          0,
          240
        );
        return {
          id: log.get("id"),
          author: member.get("displayName"),
          avatarUrl: member.get("avatarUrl"),
          timeLabel: formatDistanceToNowActivity(log),
          title,
          titlePreview: titlePreview.length < 120 ? titlePreview : `${titlePreview}...`,
          description,
          descriptionPreview:
            descriptionPreview.length < 240 ? descriptionPreview : `${descriptionPreview}...`,
        };
      })
      .toArray();

    const handleAddPrice = () => {
      if (!item.get("id")) return;
      dispatch(
        ActivityAction.showActivityDialog(item.get("id"), null, {
          item: {
            value: item.get("id"),
            label: item.getIn(["data", "name", "value"]),
            type: item.get("type"),
          },
          activityType: ActivityTypes.price,
          summary: "Price",
        })
      );
    };

    const handleEditPrice = (priceId: string) => {
      if (!itemId) return;
      dispatch(ActivityAction.showActivityDialog(itemId, priceId));
    };

    return (
      <PricesSection
        prices={prices}
        onAddPrice={handleAddPrice}
        onEditPrice={handleEditPrice}
        onDeletePrice={(priceId: string) => handDeleteLog(priceId)}
      />
    );
  };

  const renderVariations = () => {
    const variations = itemActivities
      .filter((log) => log.getIn(["event", "data", "activityType"]) === ActivityTypes.variation)
      .map((log) => {
        const member = getMember(members, log.getIn(["event", "person"]));
        const title = log.getIn(["event", "data", "summary"]) || t("Note");
        const titlePreview = title.substring(0, 120);
        const description = log.getIn(["event", "data", "description"]) || "";
        const descriptionPreview = getPlainText(description.replace(/&nbsp;/g, " ")).substring(
          0,
          240
        );
        return {
          id: log.get("id"),
          author: member.get("displayName"),
          avatarUrl: member.get("avatarUrl"),
          timeLabel: formatDistanceToNowActivity(log),
          title,
          titlePreview: titlePreview.length < 120 ? titlePreview : `${titlePreview}...`,
          description,
          descriptionPreview:
            descriptionPreview.length < 240 ? descriptionPreview : `${descriptionPreview}...`,
        };
      })
      .toArray();

    const handleAddVariation = () => {
      if (!item.get("id")) return;
      dispatch(
        ActivityAction.showActivityDialog(item.get("id"), null, {
          item: {
            value: item.get("id"),
            label: item.getIn(["data", "name", "value"]),
            type: item.get("type"),
          },
          activityType: ActivityTypes.variation,
          summary: "Variation",
        })
      );
    };

    const handleEditVariation = (priceId: string) => {
      if (!itemId) return;
      dispatch(ActivityAction.showActivityDialog(itemId, priceId));
    };

    return (
      <VariationsSection
        variations={variations}
        onAddVariation={handleAddVariation}
        onEditVariation={handleEditVariation}
        onDeleteVariation={(priceId: string) => handDeleteLog(priceId)}
      />
    );
  };

  const renderDealProduct = () => {
    const sales = fromJS(salesByProduct?.pageResult?.content || []);
    return (
      <div className="w-full flex flex-col gap-3">
        <div className="flex items-start justify-between pb-2 border-b border-gray-200">
          <h2 className="text-lg font-semibold text-gray-900">{t("Deals")}</h2>
        </div>
        <div className="flex flex-col divide-y divide-gray-100">
          {sales.map((sale) => {
            const bucket = buckets.find(
              (item) => item.get("id") === sale.getIn(["data", "bucketId", "value"])
            );
            const assignees = sale.getIn(["data", "assignees", "value"])?.toJS() || [];
            const assigneesData = assignees.map((assignee: any) => {
              const member = getMember(members, assignee);
              return {
                name: member.get("displayName"),
                avatarUrl: member.get("avatarUrl"),
              };
            });
            const displayList = assigneesData.slice(0, 2);
            const remainingCount = assigneesData.length > 2 ? assigneesData.length - 2 : 0;
            return (
              <div key={sale.get("id")} className="group flex items-center justify-between gap-2 w-full py-2">
                {/* Name & Avatar */}
                <div className="flex items-center gap-2 flex-2">
                  {sale.getIn(["data", "avatar", "value"]) ? (
                    <img
                      src={sale.getIn(["data", "avatar", "value"])}
                      alt=""
                      className="w-5 h-5 rounded-full"
                    />
                  ) : (
                    <div className="w-5 h-5 flex items-center justify-center">
                      <SquareKanbanIcon className="w-5 h-5 text-gray-400" />
                    </div>
                  )}
                  <div
                    className="truncate text-sm font-medium text-gray-900 cursor-pointer"
                    title={sale.getIn(["data", "name", "value"])}
                    onClick={() => {
                      navigate(`/workspaces/${projectId}/items/${sale.get("id")}?type=SALE`);
                    }}
                  >
                    {sale.getIn(["data", "name", "value"])}
                  </div>
                </div>

                {/* Bucket */}
                <div className="flex-1">
                  {bucket ? (
                    <div className="w-max flex items-center gap-2 py-1 px-2 rounded-full bg-gray-200">
                      <div className="w-2 h-2 rounded-full bg-gray-600"></div>
                      <div className="text-xs text-gray-600">{bucket.get("name")}</div>
                    </div>
                  ) : null}
                </div>

                {/* Assignees */}
                <div className="flex-2">
                  {assigneesData.length > 0 ? (
                    <div className="flex items-center gap-1.5 min-w-0">
                      {displayList.map((a, idx) => (
                        <div key={idx} className="flex items-center gap-1.5 min-w-0">
                          {a?.avatarUrl ? (
                            <img
                              src={a.avatarUrl}
                              alt={a.name || "avatar"}
                              className="w-5 h-5 rounded-full shrink-0"
                            />
                          ) : (
                            <div className="w-5 h-5 rounded-full bg-gray-200 shrink-0" />
                          )}
                          {a?.name && <span className="text-xs truncate">{a.name}</span>}
                        </div>
                      ))}
                      {remainingCount > 0 && (
                        <span className="text-xs text-gray-500">+{remainingCount}</span>
                      )}
                    </div>
                  ) : null}
                </div>

                {/* Estimated Revenue */}
                <div className="flex-1">
                  {formatAmount(
                    i18n.language,
                    sale.getIn(["data", "estimatedRevenue", "value"]),
                    currency,
                    "0$"
                  )}
                </div>

                {/* Estimated Probability */}
                <div className="flex-1">
                  {Math.round(sale.getIn(["data", "estimatedProbability", "value"]) || 0)}%
                </div>
              </div>
            );
          })}
          {sales.size === 0 && (
            <div className="py-8 text-center text-sm text-gray-600">{t("No deals yet")}</div>
          )}
        </div>
      </div>
    );
  };

  const renderActivityLogs = () => {
    if (activeTab.loading) {
      return (
        <div className={"w-full p-6"}>
          <SmallLoading />
        </div>
      );
    }
    if (activeTab.action === ActivityTabs.OVERVIEW.key) {
      return renderOverview();
    }
    if (activeTab.action === ActivityTabs.ACTIVITY.key) {
      return renderActivitySection();
    }
    if (activeTab.action === ActivityTabs.NOTE.key) {
      return renderNotesSection();
    }
    if (activeTab.action === ActivityTabs.EMAIL.key) {
      return renderEmailsSection();
    }
    if (activeTab.action === ActivityTabs.FILE.key) {
      return renderFilesSection();
    }
    if (activeTab.action === ActivityTabs.TASKS.key) {
      return renderTasksSection();
    }
    if (activeTab.action === ActivityTabs.PRODUCT.key) {
      return renderProduct();
    }
    if (activeTab.action === ActivityTabs.PRICE.key) {
      return renderPrices();
    }
    if (activeTab.action === ActivityTabs.VARIATION.key) {
      return renderVariations();
    }
    if (activeTab.action === ActivityTabs.DEAL_PRODUCT.key) {
      return renderDealProduct();
    }

    if (
      activeTab.action === ActivityTabs.ISSUE.key ||
      activeTab.action === ActivityTabs.CONTACT.key ||
      activeTab.action === ActivityTabs.COMPANY.key ||
      activeTab.action === ActivityTabs.TEAM.key ||
      activeTab.action === ActivityTabs.DEAL.key
    ) {
      const primaryContactId =
        item.getIn(["data", "contact", "value", "id"]) ||
        item.getIn(["data", "contact", "value", "value"]);
      const primaryAccountId =
        item.getIn(["data", "account", "value", "id"]) ||
        item.getIn(["data", "account", "value", "value"]);
      return (
        <ItemLinkView
          dataKey={activeTab.action}
          projectId={projectId}
          objectId={itemId}
          objectType={itemType}
          itemLinks={itemLinks.toJS()}
          hasPrimaryContact={!!primaryContactId}
          primaryContactId={primaryContactId || null}
          hasPrimaryAccount={!!primaryAccountId}
          primaryAccountId={primaryAccountId || null}
        />
      );
    }
    return null;
  };

  const tabItems = (): UITabItem[] => {
    return Object.values(ActivityTabs)
      .filter((tab) => tab.groups.includes(itemType))
      .filter((tab) => (tab.key === ActivityTabs.ISSUE.key ? isJira : true))
      .sort(
        (a, b) =>
          (TAB_ORDER.indexOf(a.key) === -1 ? 999 : TAB_ORDER.indexOf(a.key)) -
          (TAB_ORDER.indexOf(b.key) === -1 ? 999 : TAB_ORDER.indexOf(b.key))
      )
      .map((tab) => {
        let activityNumber = 0;
        if (tab.key === ActivityTabs.TASKS.key) {
          activityNumber = itemActivities.filter(
            (log) => log.getIn(["event", "data", "activityType"]) === ActivityTypes.task
          ).size;
        } else if (tab.key === ActivityTabs.NOTE.key) {
          activityNumber = itemActivities.filter(
            (log) => log.getIn(["event", "data", "activityType"]) === ActivityTypes.commented
          ).size;
        } else if (tab.key === ActivityTabs.EMAIL.key) {
          activityNumber = itemActivities.filter(
            (log) =>
              log.getIn(["event", "data", "activityType"]) === ActivityTypes.email ||
              log.getIn(["event", "data", "activityType"]) === ActivityTypes.logEmail ||
              log.getIn(["event", "data", "activityType"]) === ActivityTypes.sendEmail ||
              log.getIn(["event", "data", "activityType"]) === ActivityTypes.sendDraftEmail ||
              log.getIn(["event", "data", "activityType"]) === ActivityTypes.sendScheduledEmail ||
              log.getIn(["event", "data", "activityType"]) === ActivityTypes.recordSentEmail
          ).size;
        } else if (tab.key === ActivityTabs.FILE.key) {
          activityNumber = itemActivities.filter(
            (log) => log.getIn(["event", "data", "activityType"]) === ActivityTypes.file
          ).size;
        } else if (tab.key === ActivityTabs.ISSUE.key) {
          activityNumber = itemLinks.filter((log) =>
            log.getIn(["sourceObject", "id"]) === itemId
              ? log.get("targetObjectType") === "ISSUE"
              : log.get("sourceObjectType") === "ISSUE"
          ).size;
        } else if (tab.key === ActivityTabs.CONTACT.key || tab.key === ActivityTabs.TEAM.key) {
          activityNumber = itemLinks.filter((log) =>
            log.getIn(["sourceObject", "id"]) === itemId
              ? log.get("targetObjectType") === "CONTACT"
              : log.get("sourceObjectType") === "CONTACT"
          ).size;
        } else if (tab.key === ActivityTabs.COMPANY.key) {
          activityNumber = itemLinks.filter((log) =>
            log.getIn(["sourceObject", "id"]) === itemId
              ? log.get("targetObjectType") === "COMPANY" ||
                log.get("targetObjectType") === "ACCOUNT"
              : log.get("sourceObjectType") === "COMPANY" ||
                log.get("sourceObjectType") === "ACCOUNT"
          ).size;
        } else if (tab.key === ActivityTabs.DEAL.key) {
          activityNumber = itemLinks.filter((log) =>
            log.getIn(["sourceObject", "id"]) === itemId
              ? log.get("targetObjectType") === "SALE"
              : log.get("sourceObjectType") === "SALE"
          ).size;
        } else if (tab.key === ActivityTabs.PRODUCT.key) {
          const products = item.getIn(["data", "products", "value"]) || fromJS([]);
          activityNumber = products.size;
        } else if (tab.key === ActivityTabs.PRICE.key) {
          activityNumber = itemActivities.filter(
            (log) => log.getIn(["event", "data", "activityType"]) === ActivityTypes.price
          ).size;
        } else if (tab.key === ActivityTabs.VARIATION.key) {
          activityNumber = itemActivities.filter(
            (log) => log.getIn(["event", "data", "activityType"]) === ActivityTypes.variation
          ).size;
        } else if (tab.key === ActivityTabs.DEAL_PRODUCT.key) {
          activityNumber = fromJS(salesByProduct?.pageResult?.content || []).size;
        }
        return {
          key: tab.key,
          label: (
            <div className="flex items-center gap-2">
              <div className="font-medium">{t(tab.value)}</div>
              {activityNumber ? (
                <div className="min-w-4 px-1 rounded-full text-xs bg-gray-200">
                  {activityNumber}
                </div>
              ) : (
                <div></div>
              )}
            </div>
          ),
          icon: tab.icon,
        };
      });
  };

  const renderHeader = () => {
    if (showHeader) {
      return (
        <Tabs
          items={tabItems()}
          value={activeTab.action}
          onChange={(key) => {
            setActiveTab({ action: key, loading: false });
          }}
          size="md"
          headerBorderClass="border-b border-gray-200"
          headerShadow="none"
        />
      );
    }
  };

  return (
    <div className="grid grid-cols-1 gap-4">
      {renderHeader()}
      {renderActivityLogs()}
    </div>
  );
};
export default ItemActivityLogs;
